import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from './SnackbarContext';
import config from '../config';
import axios from 'axios';

const useStyles = makeStyles(() => ({
    rootContainer: {
        width: '100%',
        height: '100vh',
        position: 'relative',
        background: 'white',
        overflow: 'hidden',
        borderRadius: 30,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Nunito, Poppins, Open Sans, Inter, sans-serif',
    },
    title: {
        color: '#C084FC',
        fontSize: 18,
        fontFamily: 'Poppins',
        fontWeight: '600',
        lineHeight: '19.10px',
        wordWrap: 'break-word',
        marginBottom: 8,
        textAlign: 'center',
    },
    subtitle: {
        color: '#989898',
        fontSize: 15.28,
        fontFamily: 'Inter',
        fontWeight: '500',
        wordWrap: 'break-word',
        marginBottom: 40,
        textAlign: 'center',
    },
    otpContainer: {
        display: 'flex',
        gap: 8,
        marginBottom: 40,
        justifyContent: 'center',
        flexWrap: 'wrap',
        maxWidth: '100%',
        padding: '0 10px',
    },
    otpInput: {
        width: 50,
        height: 50,
        borderRadius: 10,
        border: '1px solid #262626',
        textAlign: 'center',
        fontSize: 18,
        fontFamily: 'Nunito',
        fontWeight: '600',
        outline: 'none',
        flexShrink: 0,
        '&:focus': {
            border: '2px solid #C084FC',
        },
    },
    resendText: {
        color: '#B6B6B6',
        fontSize: 14,
        fontFamily: 'Poppins',
        fontWeight: '500',
        textAlign: 'center',
        marginBottom: 8,
    },
    resendLink: {
        color: '#C084FC',
        fontSize: 14,
        fontFamily: 'Poppins',
        fontWeight: '500',
        textAlign: 'center',
        cursor: 'pointer',
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
}));

const MobileVerification = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const location = useLocation();
    const { showSnackbar } = useSnackbar();
    
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [isLoading, setIsLoading] = useState(false);
    const [resendTimer, setResendTimer] = useState(30);
    const [canResend, setCanResend] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const inputRefs = useRef([]);
    const otpSentRef = useRef(false);

    const { userData, role } = location.state || {};

    const sendOTP = useCallback(async () => {
        try {
            if (!userData?.phone) {
                showSnackbar('Phone number not found', 'error');
                return;
            }

            // Prevent multiple calls
            if (otpSentRef.current) {
                console.log('OTP already sent, skipping duplicate call');
                return;
            }

            console.log('Sending OTP to:', userData.phone);
            otpSentRef.current = true;
            
            const response = await axios.post(`${config.API_BASE_URL}/generate-otp`, {
                phone: userData.phone
            });

            if (response.data.message) {
                setOtpSent(true);
                showSnackbar('OTP sent to your mobile number', 'success');
            }
        } catch (error) {
            console.error('Error sending OTP:', error);
            otpSentRef.current = false; // Reset on error
            showSnackbar('Failed to send OTP', 'error');
        }
    }, [userData?.phone, showSnackbar]);

    useEffect(() => {
        // Auto-send OTP when component mounts (only once)
        if (!otpSent && userData?.phone) {
            console.log('MobileVerification mounted with userData:', userData);
            sendOTP();
        }
        
        // Start resend timer
        const timer = setInterval(() => {
            setResendTimer((prev) => {
                if (prev <= 1) {
                    setCanResend(true);
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [sendOTP, otpSent, userData?.phone]);

    const handleOtpChange = (index, value) => {
        if (value.length > 1) return;
        
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Auto-focus next input
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace') {
            // If current input has a value, clear it and stay on current input
            if (otp[index]) {
                const newOtp = [...otp];
                newOtp[index] = '';
                setOtp(newOtp);
                inputRefs.current[index]?.focus();
            }
            // If current input is empty, move to previous input and clear it
            else if (index > 0) {
                const newOtp = [...otp];
                newOtp[index - 1] = '';
                setOtp(newOtp);
                inputRefs.current[index - 1]?.focus();
            }
        }
    };

    const handleVerify = async () => {
        const otpString = otp.join('');
        if (otpString.length !== 6) {
            showSnackbar('Please enter complete OTP', 'error');
            return;
        }

        setIsLoading(true);
        try {
            if (!userData?.phone) {
                showSnackbar('Phone number not found', 'error');
                return;
            }

            // Note: /verify-otp-reset is the generic OTP verification endpoint
            // Despite the name, it's used for mobile verification in password reset flow
            console.log('Verifying OTP:', {
                phone: userData.phone,
                otpString: otpString,
                otpInt: parseInt(otpString),
                otpType: typeof parseInt(otpString)
            });
            
            const response = await axios.post(`${config.API_BASE_URL}/verify-otp-reset`, {
                phone: userData.phone,
                otp: parseInt(otpString) // Convert to integer to match database type
            });

            if (response.data.message) {
                showSnackbar('Mobile number verified successfully', 'success');
                
                // Navigate to password reset
                setTimeout(() => {
                    navigate('/password-reset', {
                        state: {
                            userData,
                            role
                        }
                    });
                }, 1000);
            }
        } catch (error) {
            console.error('Error verifying OTP:', error);
            showSnackbar(error.response?.data?.error || 'Invalid OTP', 'error');
            // Clear OTP inputs when verification fails to allow re-entry
            setOtp(['', '', '', '', '', '']);
            // Focus the first input for easy re-entry
            setTimeout(() => {
                if (inputRefs.current[0]) {
                    inputRefs.current[0].focus();
                }
            }, 100);
        } finally {
            setIsLoading(false);
        }
    };

    const handleResend = async () => {
        if (!canResend) return;
        
        setResendTimer(30);
        setCanResend(false);
        setOtpSent(false); // Reset flag to allow resend
        otpSentRef.current = false; // Reset ref to allow resend
        await sendOTP();
    };


    // Auto-submit when all OTP fields are filled
    useEffect(() => {
        if (otp.every(digit => digit !== '') && !isLoading) {
            handleVerify();
        }
    }, [otp]);

    return (
        <div className={classes.rootContainer}>
            <div className={classes.title}>
                Verify Mobile Number
            </div>
            <div className={classes.subtitle}>
                Enter the 6-digit code sent to your mobile number
            </div>

            <div className={classes.otpContainer}>
                {otp.map((digit, index) => (
                    <input
                        key={index}
                        ref={(el) => (inputRefs.current[index] = el)}
                        className={classes.otpInput}
                        type="text"
                        inputMode="numeric"
                        maxLength="1"
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        disabled={isLoading}
                    />
                ))}
            </div>

            <div className={classes.resendText}>
                Didn't receive the code?
            </div>
            {canResend ? (
                <div className={classes.resendLink} onClick={handleResend}>
                    Resend OTP
                </div>
            ) : (
                <div className={classes.resendText}>
                    Resend in {resendTimer}s
                </div>
            )}

        </div>
    );
};

export default MobileVerification;
